/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Invite Team Member & Token Link Modal (Task 10.1.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useUiStore } from '../../stores/useUiStore';
import { useTeamStore } from '../../stores/useTeamStore';
import { useWorkspaceStore } from '../../stores/useWorkspaceStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { useI18nStore } from '../../stores/useI18nStore';
import { getWorkspaceDomains } from '../../lib/urls';
import {
  UserPlus,
  X,
  Check,
  Copy,
  Mail,
  Shield,
  Eye,
  Sparkles,
} from 'lucide-react';

export const InviteMemberModal: React.FC = () => {
  const { t } = useI18nStore();
  const { activeModal, closeModal } = useUiStore();
  const { createInvitation } = useTeamStore();
  const { activeWorkspaceId, getActiveWorkspace } = useWorkspaceStore();
  const { user } = useAuthStore();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOpen = activeModal === 'invite_member';
  const activeWorkspace = getActiveWorkspace();

  if (!isOpen) return null;

  const handleGenerateInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError(t('team.emailRequired'));
      return;
    }

    if (!activeWorkspaceId) {
      setError(t('team.workspaceRequired'));
      return;
    }

    setError(null);
    const inv = createInvitation(
      activeWorkspaceId,
      email,
      role,
      user?.email || 'admin@example.com',
    );

    const origin = typeof window !== 'undefined' ? window.location.origin : getWorkspaceDomains().appOrigin;
    const link = `${origin}/invite?token=${inv.token}`;
    setGeneratedLink(link);
  };

  const handleCopyLink = () => {
    if (!generatedLink) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(generatedLink);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('member');
    setGeneratedLink(null);
    setIsCopied(false);
    setError(null);
    closeModal();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/70 backdrop-blur-md animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl border border-surface-border bg-surface p-6 sm:p-7 shadow-2xl animate-scale-in relative space-y-6">
        {/* Header Strip */}
        <div className="flex items-center justify-between border-b border-surface-border pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shadow-sm">
              <UserPlus className="w-5 h-5" />
            </div>
            <div>
              <h2 id="invite-modal-title" className="font-heading font-bold text-base text-foreground">
                {t('team.inviteTitle')}
              </h2>
              <p className="text-xs text-foreground-muted">
                {activeWorkspace?.name
                  ? t('team.inviteSubtitle', { workspace: activeWorkspace.name })
                  : t('team.roleLabel')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-500 text-xs font-medium">
            {error}
          </div>
        )}

        {!generatedLink ? (
          <form onSubmit={handleGenerateInvite} className="space-y-4">
            {/* Email Input */}
            <div className="space-y-1.5">
              <label htmlFor="invite-email" className="block text-xs font-semibold text-foreground">
                {t('team.emailLabel')}
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-foreground-muted absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  id="invite-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('team.emailPlaceholder')}
                  className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                />
              </div>
            </div>

            {/* Role Selection Matrix */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-foreground">
                {t('team.roleLabel')}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <button
                  type="button"
                  onClick={() => setRole('member')}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    role === 'member'
                      ? 'border-primary bg-primary/10 ring-1 ring-primary/40'
                      : 'border-surface-border bg-surface-elevated/60 hover:bg-surface-elevated'
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    <span>{t('team.roleMember')}</span>
                  </div>
                  <div className="text-[10px] text-foreground-muted mt-1 leading-tight">
                    {t('team.roleMemberDesc')}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setRole('admin')}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    role === 'admin'
                      ? 'border-primary bg-primary/10 ring-1 ring-primary/40'
                      : 'border-surface-border bg-surface-elevated/60 hover:bg-surface-elevated'
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <Shield className="w-3.5 h-3.5 text-primary" />
                    <span>{t('team.roleAdmin')}</span>
                  </div>
                  <div className="text-[10px] text-foreground-muted mt-1 leading-tight">
                    {t('team.roleAdminDesc')}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setRole('viewer')}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    role === 'viewer'
                      ? 'border-primary bg-primary/10 ring-1 ring-primary/40'
                      : 'border-surface-border bg-surface-elevated/60 hover:bg-surface-elevated'
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <Eye className="w-3.5 h-3.5 text-slate-400" />
                    <span>{t('team.roleViewer')}</span>
                  </div>
                  <div className="text-[10px] text-foreground-muted mt-1 leading-tight">
                    {t('team.roleViewerDesc')}
                  </div>
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 rounded-xl border border-surface-border text-xs font-medium text-foreground hover:bg-muted transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-5 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary-hover active:scale-[0.98] transition-all shadow-md flex items-center gap-1.5"
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>{t('team.generateLinkBtn')}</span>
              </button>
            </div>
          </form>
        ) : (
          /* Generated Invitation Link Success View */
          <div className="space-y-4 animate-fade-in">
            <div className="p-3.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-500 text-xs flex items-center gap-2 font-medium">
              <Check className="w-4 h-4 flex-shrink-0" />
              <span>{t('team.inviteSuccess', { email, role: role.toUpperCase() })}</span>
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-foreground">
                {t('team.inviteLinkTitle')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={generatedLink}
                  className="w-full px-3 py-2 rounded-xl border border-surface-border bg-muted/60 text-xs text-foreground font-mono select-all focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all flex-shrink-0 shadow-sm ${
                    isCopied
                      ? 'bg-emerald-500 text-white'
                      : 'bg-primary text-primary-fg hover:bg-primary-hover'
                  }`}
                >
                  {isCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{isCopied ? t('team.inviteLinkCopied') : t('team.copyInviteLink')}</span>
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary-hover transition-colors"
              >
                {t('team.complete')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
